"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, RotateCcw, Volume2, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SymptomItem {
  id: number;
  title: string;
  badge: string;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  description: string;
  causes: string;
  recommendedService: string;
  link: string;
  iconName: "Waves" | "RotateCcw" | "Volume2" | "Droplets" | "AlertTriangle" | "Flame";
}
const symptoms: SymptomItem[] = [{
  id: 1,
  title: "Slow Water Drainage",
  badge: "Common Issue",
  badgeVariant: "secondary",
  description: "Water pools around your feet in the shower or takes minutes to empty from the bathroom sink.",
  causes: "Hair buildup, soap scum residue, or early grease accumulation in the trap.",
  recommendedService: "Drain Cleaning",
  link: "/drain-cleaning/",
  iconName: "Waves"
}, {
  id: 2,
  title: "Sewage Backup & Odors",
  badge: "Urgent Hazard",
  badgeVariant: "destructive",
  description: "Foul sewer smells rising from drains, toilets bubbling, or dirty waste backing up into tubs.",
  causes: "Main sewer line blockage, tree root intrusion, or cracked underground pipe.",
  recommendedService: "Sewer Line Repair",
  link: "/sewer-line-repair/",
  iconName: "RotateCcw"
}, {
  id: 3,
  title: "Gurgling & Strange Sounds",
  badge: "Warning Sign",
  badgeVariant: "outline",
  description: "Pipes making loud gurgling, sucking, or bubbling noises whenever water empties elsewhere.",
  causes: "Trapped air pockets, blocked plumbing vent stacks, or partial main line clogs.",
  recommendedService: "Drain Cleaning",
  link: "/drain-cleaning/",
  iconName: "Volume2"
}, {
  id: 4,
  title: "Persistent Toilet Overflow",
  badge: "Emergency",
  badgeVariant: "destructive",
  description: "Toilet bowl fills to the brim and fails to flush, risking water damage across bathroom floors.",
  causes: "Heavy paper blockage, foreign objects lodged in trapway, or local vent blockage.",
  recommendedService: "Toilet Repair",
  link: "/toilet-repair/",
  iconName: "AlertTriangle"
}, {
  id: 5,
  title: "Kitchen Sink Disposal Clogs",
  badge: "High Frequency",
  badgeVariant: "secondary",
  description: "Disposal hums without spinning or stagnant greasy water refuses to drain through the unit.",
  causes: "Food debris, grease build-up, foreign object jamming blades, or worn motor.",
  recommendedService: "Garbage Disposal Repair",
  link: "/garbage-disposal-repair/",
  iconName: "Droplets"
}, {
  id: 6,
  title: "Hidden Moisture & Pipe Leaks",
  badge: "Structural Risk",
  badgeVariant: "outline",
  description: "Damp cabinets, warm spots on flooring, unexplained water bill increases, or moldy drywall.",
  causes: "Pin-hole copper leaks, loose drain fittings, or foundation pipe deterioration.",
  recommendedService: "Leak Detection Repair",
  link: "/leak-detection-repair/",
  iconName: "Flame"
}];
function renderSymptomIcon(iconName: SymptomItem["iconName"]) {
  switch (iconName) {
    case "Waves":
      return <Waves className="size-6 text-primary" />;
    case "RotateCcw":
      return <RotateCcw className="size-6 text-destructive" />;
    case "Volume2":
      return <Volume2 className="size-6 text-chart-3" />;
    case "AlertTriangle":
      return <AlertTriangle className="size-6 text-destructive" />;
    case "Droplets":
      return <Droplets className="size-6 text-primary" />;
    case "Flame":
      return <Flame className="size-6 text-accent-foreground" />;
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function DrainSymptoms() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="drainsymptomsgrid" data-nav="light" className="relative bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold">
            Diagnostic Symptom Guide
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Identify Your Drain Problem
          </h2>
          <p className="mt-4 font-default text-base text-muted-foreground sm:text-lg">
            Diagnose common plumbing issues fast. Select your symptom below to review likely root causes and get rapid professional solutions in Jonesboro.
          </p>
        </div>

        {/* Symptoms Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {symptoms.map((item, i) => <div key={item.id} data-index={i} className="flex">
              <motion.div className="w-full" initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-40px"
          }} transition={{
            duration: 0.45,
            delay: i * 0.08
          }}>
                <Card className="flex h-full flex-col justify-between border-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <div>
                    <CardHeader className="space-y-3 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex size-12 items-center justify-center rounded-lg bg-secondary">
                          {renderSymptomIcon(item.iconName)}
                        </div>
                        <Badge variant={item.badgeVariant}>
                          {item.badge}
                        </Badge>
                      </div>
                      <CardTitle className="font-default text-xl font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="font-default text-sm text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3 pt-2">
                      <div className="rounded-md border border-border/70 bg-background/60 p-3">
                        <p className="font-default text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Likely Cause
                        </p>
                        <p className="mt-1 font-default text-sm text-foreground">
                          {item.causes}
                        </p>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-3">
                    <Button asChild variant="default" className="w-full justify-between">
                      <Link to={item.link}>
                        <span>Fix With {item.recommendedService}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Emergency Help Banner */}
        <div className="mt-14 rounded-2xl border border-border bg-secondary/50 p-6 sm:p-8 md:flex md:items-center md:justify-between">
          <div className="max-w-2xl">
            <h3 className="font-default text-xl font-bold text-foreground sm:text-2xl">
              Dealing with an active plumbing emergency?
            </h3>
            <p className="mt-2 font-default text-sm text-muted-foreground sm:text-base">
              Severe backups, burst pipes, and flooding require immediate attention. Our 24/7 technicians are ready on standby across Jonesboro.
            </p>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row md:mt-0">
            <Button asChild variant="destructive" size="lg">
              <Link to="/emergency-plumbing/">
                Get Emergency Dispatch
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/problems/">
                View All Problems
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}